import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useToast } from '@shared/hooks/use-toast';
import { formatError } from '@shared/utils/RequestUtils';
import { useAuthStore } from '@stores/authStore';
import { ShippedOrdersExportApiAdapter } from '../adapters/ShippedOrdersExportApiAdapter';
import type { ExportStartParams } from '../models/export';
import { isExportDateRangeParams } from '../models/export';
import { THREE_PL_EXCEL_REPORTS, type ThreePlReportId } from '../models/reports';
import {
  clearStoredExport,
  getStoredExportRemainingMs,
  isExportInProgress,
  isExportReadyToDownload,
  loadStoredExport,
  saveStoredExport,
  updateStoredExportStatus,
  type StoredThreePlExport,
} from '../utils/pendingExportStorage';
import { waitForExport } from '../utils/waitForExport';

export function useThreePlExcelExport() {
  const { t } = useTranslation('threePlExcelReportsModule');
  const { toast } = useToast();
  const teamId = useAuthStore((s) => s.team?.id ?? null);

  const [activeExport, setActiveExport] = useState<StoredThreePlExport | null>(() =>
    loadStoredExport(useAuthStore.getState().team?.id),
  );

  const cancelledRef = useRef(false);
  const pollSessionIdRef = useRef<string | null>(null);
  const resumeStartedRef = useRef(false);

  const isExporting = activeExport != null && isExportInProgress(activeExport.status);
  const isReadyToDownload = isExportReadyToDownload(activeExport);
  const exportingReportId = isExporting ? (activeExport?.reportId ?? null) : null;

  const clearExportSession = useCallback(() => {
    pollSessionIdRef.current = null;
    setActiveExport(null);
    if (teamId != null) {
      clearStoredExport(teamId);
    }
  }, [teamId]);

  const syncActiveStatus = useCallback(
    (status: StoredThreePlExport['status']) => {
      if (status === 'completed') return;
      setActiveExport((prev) => (prev ? { ...prev, status } : null));
      if (teamId != null) {
        updateStoredExportStatus(teamId, status);
      }
    },
    [teamId],
  );

  const markExportCompleted = useCallback(
    (pending: StoredThreePlExport, urlFile: string) => {
      pollSessionIdRef.current = null;
      const completed: StoredThreePlExport = {
        ...pending,
        status: 'completed',
        urlFile,
      };
      if (teamId != null) {
        saveStoredExport(teamId, completed);
      }
      setActiveExport(completed);
    },
    [teamId],
  );

  const pollExport = useCallback(
    async (reportId: ThreePlReportId, exportId: string) => {
      const httpClient = createHttpClient(apiClientWH);
      const adapter = new ShippedOrdersExportApiAdapter(httpClient);

      return waitForExport((id) => adapter.getExportStatus(reportId, id), exportId, {
        isCancelled: () => cancelledRef.current,
        onStatusChange: (status) => syncActiveStatus(status),
      });
    },
    [syncActiveStatus],
  );

  const runPollFlow = useCallback(
    async (pending: StoredThreePlExport) => {
      const sessionKey = pending.exportId || `resume-${pending.startedAt}`;
      if (pollSessionIdRef.current === sessionKey) return false;
      pollSessionIdRef.current = sessionKey;
      cancelledRef.current = false;

      setActiveExport(pending);

      try {
        const urlFile = await pollExport(pending.reportId, pending.exportId);

        markExportCompleted(pending, urlFile);
        window.open(urlFile, '_blank');
        toast({
          title: t('export.success.title'),
          description: t('export.success.description'),
        });
        return true;
      } catch (error: unknown) {
        if (error instanceof Error && error.message === 'EXPORT_CANCELLED') {
          return false;
        }

        const message =
          error instanceof Error ? error.message : formatError(error).message || t('export.error.unexpected');

        toast({
          title: t('export.error.title'),
          description: message,
          variant: 'destructive',
        });
        clearExportSession();
        return false;
      }
    },
    [pollExport, markExportCompleted, clearExportSession, toast, t],
  );

  useEffect(() => {
    if (teamId == null || resumeStartedRef.current) return;

    const stored = loadStoredExport(teamId);
    if (!stored) return;

    resumeStartedRef.current = true;

    if (isExportReadyToDownload(stored)) {
      setActiveExport(stored);
      return;
    }

    if (!isExportInProgress(stored.status)) {
      clearStoredExport(teamId);
      return;
    }

    void runPollFlow(stored);
  }, [teamId, runPollFlow]);

  useEffect(() => {
    if (!activeExport || !isExportReadyToDownload(activeExport) || teamId == null) return;

    const remainingMs = getStoredExportRemainingMs(activeExport);
    const timeoutId = window.setTimeout(() => {
      clearExportSession();
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [activeExport, teamId, clearExportSession]);

  const cancelExport = useCallback(() => {
    cancelledRef.current = true;
    clearExportSession();
  }, [clearExportSession]);

  const dismissCompletedExport = useCallback(() => {
    clearExportSession();
  }, [clearExportSession]);

  const downloadExport = useCallback(() => {
    const url = activeExport?.urlFile?.trim();
    if (!url) return;
    window.open(url, '_blank');
  }, [activeExport?.urlFile]);

  const runExport = useCallback(
    async (reportId: ThreePlReportId, params: ExportStartParams): Promise<boolean> => {
      const reportDef = THREE_PL_EXCEL_REPORTS.find((report) => report.id === reportId);

      if (reportDef?.paramType === 'date_range') {
        if (!isExportDateRangeParams(params)) {
          toast({
            title: t('export.error.title'),
            description: t('export.error.unexpected'),
            variant: 'destructive',
          });
          return false;
        }

        if (params.date_from > params.date_to) {
          toast({
            title: t('export.error.title'),
            description: t('export.error.invalid_range'),
            variant: 'destructive',
          });
          return false;
        }
      } else if (reportDef?.paramType === 'invoice_id') {
        if (isExportDateRangeParams(params) || !params.invoice_id) {
          toast({
            title: t('export.error.title'),
            description: t('export.error.invalid_invoice'),
            variant: 'destructive',
          });
          return false;
        }
      }

      if (isExporting || pollSessionIdRef.current) {
        toast({
          title: t('export.error.title'),
          description: t('export.already_in_progress'),
          variant: 'destructive',
        });
        return false;
      }

      if (teamId == null) {
        toast({
          title: t('export.error.title'),
          description: t('export.error.unexpected'),
          variant: 'destructive',
        });
        return false;
      }

      cancelledRef.current = false;

      const pendingStart: StoredThreePlExport =
        reportDef?.paramType === 'date_range' && isExportDateRangeParams(params)
          ? {
              exportId: '',
              reportId,
              dateFrom: params.date_from,
              dateTo: params.date_to,
              startedAt: Date.now(),
              status: 'starting',
            }
          : reportDef?.paramType === 'invoice_id' && !isExportDateRangeParams(params)
            ? {
                exportId: '',
                reportId,
                invoiceId: params.invoice_id,
                invoiceLabel: params.invoice_label,
                startedAt: Date.now(),
                status: 'starting',
              }
            : {
                exportId: '',
                reportId,
                startedAt: Date.now(),
                status: 'starting',
              };

      saveStoredExport(teamId, pendingStart);
      setActiveExport(pendingStart);

      try {
        const httpClient = createHttpClient(apiClientWH);
        const adapter = new ShippedOrdersExportApiAdapter(httpClient);
        const { export_id } = await adapter.startExport(reportId, params);

        const pendingQueued: StoredThreePlExport = {
          ...pendingStart,
          exportId: export_id,
          status: 'queued',
        };
        saveStoredExport(teamId, pendingQueued);
        setActiveExport(pendingQueued);

        pollSessionIdRef.current = null;
        return (await runPollFlow(pendingQueued)) ?? false;
      } catch (error: unknown) {
        clearExportSession();

        const message =
          error instanceof Error ? error.message : formatError(error).message || t('export.error.unexpected');

        toast({
          title: t('export.error.title'),
          description: message,
          variant: 'destructive',
        });
        return false;
      }
    },
    [isExporting, teamId, runPollFlow, clearExportSession, toast, t],
  );

  return {
    runExport,
    cancelExport,
    dismissCompletedExport,
    downloadExport,
    isExporting,
    isReadyToDownload,
    exportingReportId,
    activeExport,
  };
}
